import { errorMessageFa } from '@saay/api-client';
import { normalizeDigits, normalizeSearchText, toPersianDigits } from '@saay/formatting';
import {
  type ChangeEvent,
  type CSSProperties,
  type DragEvent,
  type FormEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { DialogActions } from './actions.js';
import { Card } from './containment.js';
import { StatusChip } from './data-display.js';
import { FilterToolbar } from './filters.js';
import { Icon } from './icons.js';
import { Tabs } from './layout.js';
import { AlertDialog, RichTooltip } from './overlay-kit.js';
import { Dialog, type DialogSize } from './overlays.js';
import { Button, IconButton, Masked } from './primitives.js';
import { SearchField } from './filters.js';
import { copyableRequestId, shouldShowRequestId } from './request-id-policy.js';
import { EmptyState, RequestId, SessionExpired } from './states.js';

export { shouldShowRequestId } from './request-id-policy.js';

/** Idle warning before session expiry (X3, D-72). */
export function SessionTimeoutDialog({
  onContinue,
  onExpired,
  onOpenChange,
  open,
  secondsRemaining,
}: {
  readonly onContinue: () => void;
  readonly onExpired: () => void;
  readonly onOpenChange: (open: boolean) => void;
  readonly open: boolean;
  readonly secondsRemaining: number;
}) {
  const safeSeconds = Math.max(0, secondsRemaining);
  const progress = safeSeconds <= 0 ? 0 : safeSeconds / 60;

  useEffect(() => {
    if (open && safeSeconds === 0) {
      onExpired();
    }
  }, [onExpired, open, safeSeconds]);

  return (
    <AlertDialog
      cancelLabel="ادامهٔ کار"
      confirmLabel="خروج"
      description={
        <div className="saay-session-timeout">
          <div
            aria-hidden="true"
            className="saay-session-timeout__ring"
            style={{ '--progress': String(progress) } as CSSProperties}
          />
          <p className="saay-session-timeout__text">
            {`چون مدتی کاری انجام نداده‌اید، برای امنیت حساب تا ${toPersianDigits(safeSeconds)} ثانیهٔ دیگر از حساب خارج می‌شوید.`}
          </p>
        </div>
      }
      onConfirm={() => onOpenChange(false)}
      onOpenChange={(next) => {
        if (!next) {
          onContinue();
        }
        onOpenChange(next);
      }}
      open={open}
      title="به‌زودی از حساب خارج می‌شوید"
    />
  );
}

export function useSessionIdleWarning({
  idleMs,
  onExpire,
  onWarn,
  warningMs,
}: {
  readonly idleMs: number;
  readonly onExpire: () => void;
  readonly onWarn: () => void;
  readonly warningMs: number;
}) {
  const warnAt = Math.max(0, idleMs - warningMs);
  const [warningOpen, setWarningOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(() => Math.ceil(warningMs / 1000));
  const expiredRef = useRef(false);

  const reset = useCallback(() => {
    expiredRef.current = false;
    setWarningOpen(false);
    setSecondsLeft(Math.ceil(warningMs / 1000));
  }, [warningMs]);

  useEffect(() => {
    let warnTimer: ReturnType<typeof setTimeout> | undefined;
    let expireTimer: ReturnType<typeof setTimeout> | undefined;
    let tick: ReturnType<typeof setInterval> | undefined;

    function arm(): void {
      clearTimeout(warnTimer);
      clearTimeout(expireTimer);
      clearInterval(tick);
      if (expiredRef.current) {
        return;
      }
      reset();
      warnTimer = setTimeout(() => {
        setWarningOpen(true);
        onWarn();
        const started = Date.now();
        tick = setInterval(() => {
          const left = Math.max(0, Math.ceil(warningMs / 1000 - (Date.now() - started) / 1000));
          setSecondsLeft(left);
          if (left === 0) {
            clearInterval(tick);
          }
        }, 250);
      }, warnAt);
      expireTimer = setTimeout(() => {
        expiredRef.current = true;
        setWarningOpen(false);
        onExpire();
      }, idleMs);
    }

    const events = ['pointerdown', 'keydown', 'scroll', 'touchstart'] as const;
    for (const event of events) {
      window.addEventListener(event, arm, { passive: true });
    }
    arm();
    return () => {
      for (const event of events) {
        window.removeEventListener(event, arm);
      }
      clearTimeout(warnTimer);
      clearTimeout(expireTimer);
      clearInterval(tick);
    };
  }, [idleMs, onExpire, onWarn, reset, warnAt, warningMs]);

  return {
    continueSession: reset,
    secondsRemaining: secondsLeft,
    setWarningOpen,
    warningOpen,
  };
}

/** Command form in a dialog (X5): submit only when dirty; tooltip explains a blocked submit. */
export function CommandFormDialog({
  children,
  description,
  dirty,
  loading = false,
  onOpenChange,
  onSubmit,
  open,
  size = 'wide',
  submitLabel,
  title,
}: {
  readonly children: ReactNode;
  readonly description?: ReactNode;
  readonly dirty: boolean;
  readonly loading?: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSubmit: () => void;
  readonly open: boolean;
  readonly size?: DialogSize;
  readonly submitLabel: string;
  readonly title: ReactNode;
}) {
  const formId = useId();
  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!dirty || loading) {
      return;
    }
    onSubmit();
  }

  const submitButton = (
    <Button
      disabled={!dirty || loading}
      form={formId}
      loading={loading}
      type="submit"
      variant="primary"
    >
      {submitLabel}
    </Button>
  );

  return (
    <Dialog
      description={description}
      footer={
        <DialogActions
          primary={
            dirty ? (
              submitButton
            ) : (
              <RichTooltip
                description="پس از تغییر حداقل یک فیلد می‌توانید ثبت کنید."
                title="ثبت غیرفعال است"
              >
                <span>{submitButton}</span>
              </RichTooltip>
            )
          }
          secondary={
            <Button onClick={() => onOpenChange(false)} type="button" variant="ghost">
              انصراف
            </Button>
          }
        />
      }
      onOpenChange={onOpenChange}
      open={open}
      size={size}
      stickyFooter
      title={title}
    >
      <form className="saay-command-form" id={formId} noValidate onSubmit={handleSubmit}>
        {children}
      </form>
    </Dialog>
  );
}

export type FilterBarChip = {
  readonly id: string;
  readonly label: ReactNode;
  readonly onRemove: () => void;
};

/** List filter bar (X6): search landmark, active filter chips, and «پاک‌کردن همه». */
export function FilterBar({
  activeFilters,
  children,
  label,
  message,
  onClearAll,
  onSubmit,
  trailing,
}: {
  readonly activeFilters?: readonly FilterBarChip[];
  readonly children: ReactNode;
  readonly label: string;
  readonly message?: string | undefined;
  readonly onClearAll?: () => void;
  readonly onSubmit: () => void;
  readonly trailing?: ReactNode;
}) {
  const filters = activeFilters ?? [];
  const showClear = filters.length > 0 && onClearAll !== undefined;
  return (
    <div className="saay-filter-bar">
      <FilterToolbar
        label={label}
        {...(message === undefined ? {} : { message })}
        onSubmit={onSubmit}
        trailing={trailing}
      >
        {children}
      </FilterToolbar>
      {filters.length === 0 && !showClear ? null : (
        <div className="saay-filter-bar__chips" role="list">
          {filters.map((chip) => (
            <span className="saay-filter-bar__chip" key={chip.id} role="listitem">
              <span>{chip.label}</span>
              <button
                aria-label={`حذف فیلتر ${chip.id}`}
                className="saay-filter-bar__chip-remove saay-state"
                onClick={chip.onRemove}
                type="button"
              >
                <Icon name="close" />
              </button>
            </span>
          ))}
          {showClear ? (
            <button
              className="saay-filter-bar__clear saay-state"
              onClick={onClearAll}
              type="button"
            >
              پاک‌کردن همه
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}

/** Detail screen header (X6, Q7): sticky status chip and tabs under the title. */
export function DetailPageHeader<K extends string>({
  actions,
  description,
  eyebrow,
  onTabChange,
  panel,
  status,
  tabs,
  tabValue,
  title,
}: {
  readonly actions?: ReactNode;
  readonly description?: ReactNode;
  readonly eyebrow?: ReactNode;
  readonly onTabChange: (key: K) => void;
  readonly panel: ReactNode;
  readonly status?: ReactNode;
  readonly tabs: readonly { readonly key: K; readonly label: string }[];
  readonly tabValue: K;
  readonly title: ReactNode;
}) {
  return (
    <div className="saay-detail-page">
      <div className="saay-detail-page__sticky">
        <header className="saay-detail-page__header">
          <div>
            {eyebrow === undefined ? null : <span className="saay-eyebrow">{eyebrow}</span>}
            <h1>{title}</h1>
            {description === undefined ? null : (
              <p className="saay-page-header__description">{description}</p>
            )}
          </div>
          <div className="saay-detail-page__meta">
            {status}
            {actions === undefined ? null : <div className="saay-actions">{actions}</div>}
          </div>
        </header>
        <Tabs<K>
          label="بخش‌های جزئیات"
          onChange={onTabChange}
          panel={null}
          tabs={tabs}
          value={tabValue}
        />
      </div>
      <div className="saay-detail-page__panel">{panel}</div>
    </div>
  );
}

export type EntityPickerOption = {
  readonly description?: ReactNode;
  readonly disabled?: boolean;
  readonly disabledReason?: string;
  readonly id: string;
  readonly label: ReactNode;
  readonly maskedValue?: string;
};

/** Async entity picker (X7) with preview card for the selected row. */
export function EntityPicker({
  emptyMessage = 'نتیجه‌ای نیست.',
  error,
  label,
  loading = false,
  onQueryChange,
  onSelect,
  options,
  placeholder,
  query,
  selectedId,
}: {
  readonly emptyMessage?: string;
  readonly error?: ReactNode;
  readonly label: string;
  readonly loading?: boolean;
  readonly onQueryChange: (query: string) => void;
  readonly onSelect: (id: string | null) => void;
  readonly options: readonly EntityPickerOption[];
  /** What to type; the label when omitted (an empty search field gave no hint). */
  readonly placeholder?: string;
  readonly query: string;
  readonly selectedId: string | null;
}) {
  const selected = options.find((option) => option.id === selectedId) ?? null;
  const listId = useId();

  return (
    <div className="saay-entity-picker">
      <SearchField
        {...(options.length === 0 ? {} : { 'aria-controls': listId })}
        label={label}
        onChange={(event) => onQueryChange(event.target.value)}
        placeholder={placeholder ?? label}
        value={query}
      />
      {error === undefined ? null : (
        <p className="saay-entity-picker__error" role="alert">
          {error}
        </p>
      )}
      {selected === null ? null : (
        <Card className="saay-entity-picker__preview" variant="outlined">
          <strong>{selected.label}</strong>
          {selected.description === undefined ? null : <p>{selected.description}</p>}
          {selected.maskedValue === undefined ? null : (
            <p>
              <Masked kind="mobile" value={selected.maskedValue} />
            </p>
          )}
          <Button onClick={() => onSelect(null)} size="sm" variant="ghost">
            تغییر
          </Button>
        </Card>
      )}
      {options.length === 0 ? (
        loading || query.trim() !== '' ? (
          <p
            aria-busy={loading || undefined}
            className="saay-entity-picker__hint"
            id={listId}
            role="status"
          >
            {loading ? 'در حال جست‌وجو…' : emptyMessage}
          </p>
        ) : null
      ) : (
        <ul
          aria-busy={loading || undefined}
          className="saay-entity-picker__list"
          id={listId}
          role="listbox"
        >
          {options.map((option) => (
            <li key={option.id} role="presentation">
              <button
                aria-selected={option.id === selectedId}
                className="saay-entity-picker__option saay-state"
                disabled={option.disabled === true}
                onClick={() => onSelect(option.id)}
                role="option"
                title={option.disabledReason}
                type="button"
              >
                <span>{option.label}</span>
                {option.description === undefined ? null : (
                  <span className="saay-entity-picker__option-desc">{option.description}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export type CityOption = {
  readonly cityLabel: string;
  readonly id: string;
  readonly provinceLabel: string;
};

function cityDisplay(option: CityOption): string {
  return `${option.cityLabel}، ${option.provinceLabel}`;
}

/** City + province picker (X8, D-71): one search over «شهر، استان». */
export function CityPicker({
  homeCityId,
  label = 'شهر',
  onChange,
  options,
  value,
}: {
  readonly homeCityId?: string;
  readonly label?: string;
  readonly onChange: (id: string | null) => void;
  readonly options: readonly CityOption[];
  readonly value: string | null;
}) {
  const [query, setQuery] = useState('');
  const normalized = normalizeSearchText(query);
  const filtered = useMemo(() => {
    if (normalized === '') {
      return options.slice(0, 12);
    }
    return options.filter((option) =>
      normalizeSearchText(cityDisplay(option)).includes(normalized),
    );
  }, [normalized, options]);

  const selected = options.find((option) => option.id === value) ?? null;

  return (
    <div className="saay-city-picker">
      {homeCityId === undefined ? null : (
        <button
          className="saay-city-picker__home saay-state"
          onClick={() => onChange(homeCityId)}
          type="button"
        >
          شهر من
        </button>
      )}
      <SearchField
        label={label}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="شهر یا استان"
        value={query}
      />
      {selected === null ? null : (
        <p className="saay-city-picker__selected">
          انتخاب‌شده: <strong>{cityDisplay(selected)}</strong>
        </p>
      )}
      <ul aria-label={label} className="saay-city-picker__list" role="listbox">
        {filtered.map((option) => (
          <li key={option.id} role="none">
            <button
              aria-selected={option.id === value}
              className="saay-city-picker__option saay-state"
              onClick={() => {
                onChange(option.id);
                setQuery('');
              }}
              role="option"
              type="button"
            >
              {cityDisplay(option)}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Read-to-end agreement (X12, D-20): accept enabled only after scrolling to the end. */
export function AgreementReader({
  acceptLabel = 'می‌پذیرم',
  children,
  onAccept,
  versionLabel,
}: {
  readonly acceptLabel?: string;
  readonly children: ReactNode;
  readonly onAccept: () => void;
  readonly versionLabel?: ReactNode;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(0);
  const [atEnd, setAtEnd] = useState(false);

  const onScroll = useCallback(() => {
    const node = scrollRef.current;
    if (node === null) {
      return;
    }
    const max = node.scrollHeight - node.clientHeight;
    const next = max <= 0 ? 1 : node.scrollTop / max;
    setProgress(next);
    setAtEnd(next >= 0.98);
  }, []);

  return (
    <section className="saay-agreement-reader">
      <div className="saay-agreement-reader__head">
        {versionLabel === undefined ? null : (
          <StatusChip title={String(versionLabel)} value="version" />
        )}
        <LinearAgreementProgress value={progress} />
      </div>
      <div className="saay-agreement-reader__body" onScroll={onScroll} ref={scrollRef}>
        {children}
      </div>
      <Button disabled={!atEnd} onClick={onAccept} variant="primary">
        {acceptLabel}
      </Button>
    </section>
  );
}

function LinearAgreementProgress({ value }: { readonly value: number }) {
  const percent = Math.min(100, Math.max(0, Math.round(value * 100)));
  return (
    <div
      aria-label="پیشرفت خواندن"
      aria-valuemax={100}
      aria-valuemin={0}
      aria-valuenow={percent}
      className="saay-agreement-reader__progress"
      role="progressbar"
    >
      <span style={{ inlineSize: `${percent}%` }} />
    </div>
  );
}

/** Camera permission recovery (X13). */
export function CameraPermissionRecovery({ onRequest }: { readonly onRequest: () => void }) {
  const [helpOpen, setHelpOpen] = useState(false);
  return (
    <EmptyState
      action={
        <div className="saay-camera-permission__actions">
          <Button onClick={onRequest}>اجازهٔ دوربین</Button>
          <Button onClick={() => setHelpOpen((open) => !open)} variant="ghost">
            چطور؟
          </Button>
        </div>
      }
      description={
        helpOpen
          ? 'در تنظیمات مرورگر یا دستگاه، دسترسی دوربین را برای این برنامه فعال کنید و صفحه را دوباره باز کنید.'
          : undefined
      }
      icon="qr"
      title="دسترسی به دوربین داده نشده است"
    />
  );
}

export type ImportRowStatus = 'valid' | 'invalid' | 'duplicate';

export type FileImportPreviewRow = {
  readonly cells: readonly string[];
  readonly key: string;
  readonly status: ImportRowStatus;
  readonly statusLabel: string;
};

export type FileImportPreview = {
  readonly columns: readonly string[];
  readonly invalidCount: number;
  readonly rows: readonly FileImportPreviewRow[];
  readonly validCount: number;
};

/** CSV / paste import with preview table (I11). */
export function FileImportField({
  accept = '.csv,text/csv',
  label,
  onFile,
  onPaste,
  preview,
  templateHref,
}: {
  readonly accept?: string;
  readonly label: string;
  readonly onFile: (file: File) => void;
  readonly onPaste?: (text: string) => void;
  readonly preview: FileImportPreview | null;
  readonly templateHref?: string;
}) {
  const inputId = useId();
  const [dragOver, setDragOver] = useState(false);

  function readFile(file: File | undefined) {
    if (file !== undefined) {
      onFile(file);
    }
  }

  function onDrop(event: DragEvent) {
    event.preventDefault();
    setDragOver(false);
    readFile(event.dataTransfer.files[0]);
  }

  return (
    <div className="saay-file-import">
      <label className="saay-file-import__drop" data-drag={dragOver || undefined} htmlFor={inputId}>
        <input
          accept={accept}
          className="saay-visually-hidden"
          id={inputId}
          onChange={(event: ChangeEvent<HTMLInputElement>) => readFile(event.target.files?.[0])}
          type="file"
        />
        <p>{label}</p>
        {templateHref === undefined ? null : (
          <a className="saay-file-import__template" href={templateHref}>
            دانلود قالب
          </a>
        )}
      </label>
      <div
        className="saay-file-import__paste"
        onDragLeave={() => setDragOver(false)}
        onDragOver={(event) => {
          event.preventDefault();
          setDragOver(true);
        }}
        onDrop={onDrop}
        onPaste={(event) => {
          const text = event.clipboardData.getData('text');
          if (text !== '' && onPaste !== undefined) {
            event.preventDefault();
            onPaste(text);
          }
        }}
        aria-label="چسباندن از کلیپ‌بورد"
        role="textbox"
        tabIndex={0}
      >
        فایل را بکشید و رها کنید یا از کلیپ‌بورد بچسبانید.
      </div>
      {preview === null ? null : (
        <div className="saay-file-import__summary">
          {`${toPersianDigits(preview.validCount)} معتبر · ${toPersianDigits(preview.invalidCount)} خطا`}
        </div>
      )}
      {preview === null ? null : (
        <table className="saay-file-import__table">
          <caption className="saay-visually-hidden">پیش‌نمایش فایل</caption>
          <thead>
            <tr>
              {preview.columns.map((column) => (
                <th key={column}>{column}</th>
              ))}
              <th>وضعیت</th>
            </tr>
          </thead>
          <tbody>
            {preview.rows.map((row) => (
              <tr data-status={row.status} key={row.key}>
                {row.cells.map((cell, index) => (
                  <td key={`${row.key}-${index}`}>{cell}</td>
                ))}
                <td>
                  <StatusChip title={row.statusLabel} value={row.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export type EditableGridColumn<Row> = {
  readonly header: ReactNode;
  readonly id: string;
  readonly renderCell: (row: Row, onChange: (next: Row) => void) => ReactNode;
};

/**
 * Editable rows for enrollment-style lists (I11 / admin rows editor). Each row is a card with its
 * labelled fields in a responsive grid, so the fields never squeeze into narrow table columns (owner).
 */
export function EditableGrid<Row extends { readonly id: string }>({
  addLabel = 'افزودن ردیف',
  caption,
  columns,
  onAdd,
  onChange,
  onRemove,
  rows,
}: {
  readonly addLabel?: string;
  readonly caption: string;
  readonly columns: readonly EditableGridColumn<Row>[];
  readonly onAdd: () => void;
  readonly onChange: (row: Row) => void;
  readonly onRemove: (id: string) => void;
  readonly rows: readonly Row[];
}) {
  return (
    <div className="saay-editable-grid">
      <ol aria-label={caption} className="saay-editable-grid__rows">
        {rows.map((row, index) => {
          const rowLabel = `ردیف ${toPersianDigits(String(index + 1))}`;
          return (
            <li aria-label={rowLabel} className="saay-editable-grid__row" key={row.id}>
              <div className="saay-editable-grid__row-header">
                <span className="saay-editable-grid__row-title">{rowLabel}</span>
                <IconButton
                  icon="trash"
                  label={`حذف ${rowLabel}`}
                  onClick={() => onRemove(row.id)}
                  variant="standard"
                />
              </div>
              <div className="saay-editable-grid__fields">
                {columns.map((column) => (
                  <div className="saay-editable-grid__field" key={column.id}>
                    {column.renderCell(row, (next) => onChange(next))}
                  </div>
                ))}
              </div>
            </li>
          );
        })}
      </ol>
      <Button onClick={onAdd} variant="secondary">
        {addLabel}
      </Button>
    </div>
  );
}

/** Unknown command outcome with optional technical request ID (Q12). */
export function UnknownOutcomeState({
  checking = false,
  error,
  onCheckStatus,
}: {
  readonly checking?: boolean;
  readonly error?: unknown;
  readonly onCheckStatus: () => void;
}) {
  const requestId = error === undefined ? null : copyableRequestId(error);
  return (
    <section className="saay-panel" data-tone="warning" role="alert">
      <h2 className="saay-panel__title">نتیجه مشخص نیست</h2>
      <p className="saay-panel__text">
        {error === undefined
          ? 'نتیجه درخواست مشخص نیست. پیش از تلاش دوباره، وضعیت را بررسی کنید.'
          : errorMessageFa(error)}
      </p>
      {requestId === null ? null : <RequestId value={requestId} />}
      <Button loading={checking} onClick={onCheckStatus} variant="secondary">
        بررسی وضعیت
      </Button>
    </section>
  );
}

/** Review summary before a financial command (X7/X8). */
export function ConfirmationReview({
  children,
  onConfirm,
  submitLabel,
  title,
}: {
  readonly children: ReactNode;
  readonly onConfirm: () => void;
  readonly submitLabel: string;
  readonly title: ReactNode;
}) {
  return (
    <section className="saay-confirmation-review">
      <h2>{title}</h2>
      <div className="saay-confirmation-review__body">{children}</div>
      <Button onClick={onConfirm} variant="primary">
        {submitLabel}
      </Button>
    </section>
  );
}

/** Session expired after idle timeout: return to the same route (X3). */
export function SessionTimeoutExpired({ onLogin }: { readonly onLogin: () => void }) {
  return <SessionExpired onLogin={onLogin} />;
}

/** Parses pasted CSV / TSV into rows with Persian digit normalization (I11). */
export function parseDelimitedImport(text: string): readonly string[][] {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line !== '');
  return lines.map((line) => line.split(/[,;\t]/).map((cell) => normalizeDigits(cell.trim())));
}
